/* eslint-disable react/jsx-no-target-blank */
import React, { Fragment } from 'react';

// 3rdparty
import { FormattedNumber } from 'react-intl';

// components
import AlertMessage from '../../../../components/AlertMessage';
import Icon from '../../../../components/Icon';
import LoadingIndicator from '../../../../components/LoadingIndicator';

// enums
import { orderByTypes } from '../../receipts-store';

// css
import { CheckCircle, HighlightOff } from '@mui/icons-material';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import EmptyState from '../../../../components/EmptyState';
import { imageStatus } from '../../../Tracking/components/Details/Receipts';
import { ButtonViewReceipt, CellContent, CellWrapper } from './styles';
import './styles.css';

const ReceiptsTable = ({
  receipts,
  isFetching,
  error,
  onLocationClick,
  currentOrder,
  onOrderChange,
  onRetry,
}) => {
  const isPhotoActive = ({ photos }) => photos && photos.length > 0 && photos[0].active === imageStatus.ACTIVE;

  return (
    <Fragment>
      {/* ======================= */}
      {/* ===== ERROR STATE ===== */}
      {/* ======================= */}
      {error && !isFetching && (
        <AlertMessage
          message={error}
          box
          error
          onRetry={onRetry}
          actionText="Tentar Novamente"
        />
      )}

      {/* ========================= */}
      {/* ===== LOADING STATE ===== */}
      {/* ========================= */}
      {isFetching && (
        <LoadingIndicator
          theme="dark"
          text="Carregando comprovantes"
          className="ReceiptsTable__loading"
        />
      )}

      {/* ========================= */}
      {/* ======= EMPTY STATE ===== */}
      {/* ========================= */}
      {receipts.length === 0 && !isFetching && !error && (
        <EmptyState
          className="ReceiptsTable__empty"
          message="Nenhum comprovante encontrado para o período selecionado"
        />
      )}

      {/* ======================== */}
      {/* ===== NORMAL STATE ===== */}
      {/* ======================== */}
      {!isFetching && !error && receipts.length > 0 && (
        <div className="ReceiptsTable__wrapper">
          <table className="ReceiptsTable">
            <thead>
              <tr>
                <th className="ReceiptsTable__header">Produto</th>
                <th className="ReceiptsTable__header">Conhecimento</th>
                <th className="ReceiptsTable__header">Nota Fiscal</th>
                <th className="ReceiptsTable__header">Valor (R$)</th>
                <th className="ReceiptsTable__header">Status</th>
                <th className="ReceiptsTable__header">
                  Canhoto CTE
                  {currentOrder === orderByTypes.NO_ORDER && (
                    <Icon
                      name="caret-up-down"
                      className="ReceiptsTable__header-order-by"
                      onClick={() => {
                        onOrderChange(orderByTypes.ASCENDING);
                      }}
                    />
                  )}
                  {currentOrder === orderByTypes.DESCENDING && (
                    <Icon
                      name="arrow_drop_down"
                      className="ReceiptsTable__header-order-by"
                      onClick={() => {
                        onOrderChange(orderByTypes.NO_ORDER);
                      }}
                    />
                  )}
                  {currentOrder === orderByTypes.ASCENDING && (
                    <Icon
                      name="arrow_drop_up"
                      className="ReceiptsTable__header-order-by"
                      onClick={() => {
                        onOrderChange(orderByTypes.DESCENDING);
                      }}
                    />
                  )}
                </th>
                <th className="ReceiptsTable__header">Local</th>
              </tr>
            </thead>

            <tbody>
              {receipts.map(receipt => (
                <tr key={receipt.nf} className="ReceiptsTable__row">
                  <td className="ReceiptsTable__cell ReceiptsTable__cell--highlighted">
                    {receipt.productName}
                  </td>
                  <td className="ReceiptsTable__cell">{receipt.cteNumber}</td>
                  <td className="ReceiptsTable__cell">
                    {receipt.nf}/{receipt.nfGroup}
                  </td>
                  <td className="ReceiptsTable__cell">
                    <FormattedNumber
                      // -disable-next-line react/style-prop-object
                      style="currency"
                      minimumFractionDigits={2}
                      maximumFractionDigits={2}
                      currency="BRL"
                      currencyDisplay="symbol"
                      value={receipt.receiptValue}
                      useGrouping
                    />
                  </td>
                  <td className="ReceiptsTable__cell">
                    <div className="ReceiptsTable__cell-status-container">
                      {isPhotoActive(receipt) ? (
                        <>
                          <CheckCircle color='success' sx={{ fontSize: 12 }} />
                          Ativo
                        </>
                      ) : (
                        <>
                          <HighlightOff sx={{ fontSize: 12 }} />
                          Inativo
                        </>
                      )}
                    </div>
                  </td>
                  {isPhotoActive(receipt) ? (
                    <td className="ReceiptsTable__cell ReceiptsTable__cell--has-link">
                      <ButtonViewReceipt 
                        to={{ 
                          pathname:`/u/tracking/${receipt.nf}`,
                          state: {
                            openDrawerInReceiptExclusion: true,
                          }   
                        }}
                        style={{ textDecoration: 'none', width: '25%' }}
                      >
                        <CellContent>
                          <ImageOutlinedIcon htmlColor='var(--luft-primary)' sx={{ fontSize: 14 }} />
                          <span>Comprovante</span>
                        </CellContent>
                      </ButtonViewReceipt>
                    </td>
                  ) : (
                    <td className="ReceiptsTable__cell">
                      <CellWrapper>
                        <CellContent>
                          <ImageNotSupportedOutlinedIcon htmlColor='var(--luft-primary)' sx={{ fontSize: 14 }} />
                          <span>Sem Comprovante</span>
                        </CellContent>
                      </CellWrapper>
                    </td>
                  )}
                  {receipt.location.autoTrackingEnabled && (
                    <td className="ReceiptsTable__cell ReceiptsTable__cell--has-link">
                      <Icon className="ReceiptsTable__cell-icon" name="map" />
                      <button
                        onClick={() => {
                          onLocationClick(
                            {
                              lat: receipt.location.latitude,
                              lng: receipt.location.longitude,
                            },
                            receipt.location.locationAddress,
                          );
                        }}
                        className="ReceiptsTable__cell-link"
                      >
                        Ver no mapa
                      </button>
                    </td>
                  )}

                  {!receipt.location.autoTrackingEnabled && (
                    <td className="ReceiptsTable__cell">Sem autotracking</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Fragment>
  );
}

export default ReceiptsTable;
